import React, { useState, useEffect } from 'react';
import { adminCreateCert, adminUpdateCert, adminDeleteCert, getCertifications as fetchCerts } from '../../api/client';
import { Plus, Trash2, Save, Loader2, Edit3, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const inputStyle = { background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', color: '#f2f2ff', fontFamily: "'DM Sans', sans-serif", fontSize: 14, borderRadius: 3 };
const labelStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, color: '#6a6a88', marginBottom: 6, display: 'block' };
const empty = { name: '', issuer: '', year: new Date().getFullYear().toString(), color: '#00e5ff', icon: 'network', order: 0 };
const icons = ['network', 'hardware', 'software', 'support', 'cloud'];

export default function CertificationsManager() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); try { setCerts(await fetchCerts()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ ...empty, order: certs.length + 1 }); setEditing(null); setShowForm(true); };
  const openEdit = (c) => { setForm(c); setEditing(c.id); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(empty); };

  const handleSave = async () => {
    if (!form.name || !form.issuer) { toast.error('Nama dan penerbit wajib diisi.'); return; }
    setSaving(true);
    try {
      const data = { name: form.name, issuer: form.issuer, year: form.year, color: form.color, icon: form.icon, order: Number(form.order) };
      if (editing) { await adminUpdateCert(editing, data); toast.success('Sertifikasi diperbarui!'); }
      else { await adminCreateCert(data); toast.success('Sertifikasi ditambahkan!'); }
      closeForm(); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Hapus sertifikasi "${name}"?`)) return;
    try { await adminDeleteCert(id); load(); toast.success('Dihapus.'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontWeight: 700, fontSize: 22, color: '#f2f2ff', margin: '0 0 4px', letterSpacing: '-0.3px' }}>Manajemen Sertifikasi</h1>
          <p style={{ fontSize: 13, color: '#6a6a88', margin: 0 }}>{certs.length} sertifikasi terdaftar</p>
        </div>
        <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 3, background: 'linear-gradient(135deg, #00e5ff, #9b00ff)', color: '#fff', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', boxShadow: 'none' }}><Plus size={16} /> Tambah Sertifikasi</button>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'rgba(19,14,42,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: 32, width: '100%', maxWidth: 500 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontWeight: 800, fontSize: 20, color: '#f2f2ff', margin: 0 }}>{editing ? 'Edit Sertifikasi' : 'Tambah Sertifikasi'}</h2>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', color: '#6a6a88', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div style={{ gridColumn: '1/-1' }}><Label style={labelStyle}>Nama Sertifikasi *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder='e.g. MTCNA' style={inputStyle} /></div>
              <div><Label style={labelStyle}>Penerbit *</Label><Input value={form.issuer} onChange={e => setForm(p => ({ ...p, issuer: e.target.value }))} placeholder='Mikrotik' style={inputStyle} /></div>
              <div><Label style={labelStyle}>Tahun</Label><Input value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))} placeholder='2023' style={inputStyle} /></div>
              <div>
                <Label style={labelStyle}>Warna Badge</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type='color' value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} style={{ width: 40, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'none' }} />
                  <Input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                </div>
              </div>
              <div>
                <Label style={labelStyle}>Icon</Label>
                <select value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} style={{ ...inputStyle, width: '100%', padding: '10px 12px' }}>
                  {icons.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div><Label style={labelStyle}>Urutan</Label><Input type='number' value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} style={inputStyle} /></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSave} disabled={saving} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 3, background: 'linear-gradient(135deg, #00e5ff, #9b00ff)', color: '#fff', fontWeight: 700, fontSize: 14, border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />} {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
              <button onClick={closeForm} style={{ padding: '12px 20px', borderRadius: 3, background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#6a6a88', cursor: 'pointer', fontWeight: 600 }}>Batal</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#6a6a88', gap: 10 }}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />Memuat...</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {certs.map(cert => (
            <div key={cert.id} style={{ background: 'var(--card)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, padding: 20, position: 'relative', overflow: 'hidden', transition: 'border-color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,229,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(168,85,247,0.12)'}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: cert.color, borderRadius: '14px 14px 0 0' }} />
              <div style={{ width: 44, height: 44, borderRadius: 3, background: `${cert.color}18`, border: `1px solid ${cert.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: cert.color }}>{cert.name.charAt(0)}</span>
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#f2f2ff', marginBottom: 4, lineHeight: 1.4 }}>{cert.name}</div>
              <div style={{ fontSize: 12, color: '#6a6a88', marginBottom: 12 }}>{cert.issuer} · {cert.year}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => openEdit(cert)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '7px', borderRadius: 7, background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#00e5ff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}><Edit3 size={12} /> Edit</button>
                <button onClick={() => handleDelete(cert.id, cert.name)} style={{ padding: '7px 12px', borderRadius: 7, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
